// Product analytics (ANALYTICS → Setup rules). The only module that talks to PostHog: components
// call `track`, never `posthog.capture`. The typed event map is the allow-list of properties:
// ids, slugs, enums, counts and booleans only — never names, notes, message text or coordinates.
import { useEffect } from 'react';

import { campaignTag, notificationFromStartParam, type NotificationType } from '@spot/shared';

import { parseStartParam } from './deep-link';

export interface WebEvents {
	app_opened: {
		is_new_user: boolean;
		platform: string | null;
		tg_version: string | null;
		entry: 'deeplink' | 'direct';
	};
	event_detail_opened: {
		event_id: string;
		category: string;
		entry: EventEntry;
	};
	application_submitted: {
		event_id: string;
		accept_mode: 'auto' | 'manual';
		has_note: boolean;
		status: 'pending' | 'accepted';
	};
	bot_access_result: { granted: boolean; trigger_action: 'apply' };
	bot_access_requested: { trigger_action: 'apply' };
	report_submitted: { target_type: 'event' | 'user' | 'message'; reason: string };
	user_blocked: { target_type: 'user' };
	/** First-launch consent screen (T-007); `repeat`: a new Terms version, birth date known. */
	consent_shown: { repeat: boolean };
	consent_accepted: { repeat: boolean };
	map_viewed: { city: string; pins_count: number };
	filter_changed: { filter: 'category' | 'when'; value: string };
	/** One-tap «Зараз поруч» switched on (T-208). */
	now_mode_opened: { city: string };
	/** «N нових подій у вашому місті» appeared / was tapped (T-1334). */
	new_events_toast_shown: { city: string; count: number };
	new_events_toast_clicked: { city: string; count: number };
	locate_tapped: Record<string, never>;
	location_permission_result: { granted: boolean };
	pin_tapped: { event_id: string; category: string; distance_bucket: DistanceBucket };
	/** The S2 peek card (map). */
	event_card_opened: { event_id: string; category: string; entry: EventEntry };
	event_shared: { event_id: string };
	event_saved: { event_id: string };
	apply_started: { event_id: string; accept_mode: 'auto' | 'manual' };
	event_create_started: Record<string, never>;
	/** Furthest part of the S7 form with user input: what (title/category), where, about. */
	event_create_abandoned: { step_reached: CreateStep };
	chat_opened: { event_id: string };
	message_sent: { event_id: string };
	quick_status_sent: { event_id: string; kind: 'arrived' | 'late' };
	/** `bot`: a bot button (`-n<code>` link); `center`: the in-app list (S15). */
	notification_clicked: { type: NotificationType; channel: 'bot' | 'center' };
	/** An API error reached the UI; `screen` is the route pattern, never a URL with ids. */
	error_shown: { code: string; screen: string };
}

export type DistanceBucket = 'lt_1km' | '1_3km' | '3_10km' | 'gt_10km' | 'unknown';
export type CreateStep = 'none' | 'what' | 'where' | 'about';

export type EventEntry = 'map' | 'list' | 'deeplink' | 'other';

/** The slice of the PostHog client this app uses (tests pass a fake). */
export interface AnalyticsClient {
	capture(event: string, properties: Record<string, unknown>): void;
	identify(distinctId: string): void;
	sessionRecordingStarted(): boolean;
	startSessionRecording(): void;
	stopSessionRecording(): void;
}

interface State {
	client: AnalyticsClient;
	/** Sent with the first event of the session, then cleared. */
	source: string | null;
	platform: string | null;
	version: string | null;
	/** A bot notification button opened the app: counted as `notification_clicked`. */
	viaNotification: NotificationType | null;
	/** The event a deep link opened; its first detail view counts as `entry: deeplink`. */
	deepLinkEventId: string | null;
	fromDeepLink: boolean;
	replayBlocks: number;
	/** Whether replay was running when the first block started (restart only then). */
	replayWasOn: boolean;
}

let state: State | null = null;

/**
 * `source` from `startapp`: event links are shares, other deep links come from bot messages,
 * anything else is a campaign tag (e.g. `blogger_xyz`).
 */
export function sourceFromStartParam(startParam: string | null): string {
	if (startParam === null || startParam === '') return 'direct';
	const link = parseStartParam(startParam);
	if (notificationFromStartParam(startParam) !== null) return 'notification';
	if (link !== null) return link.kind === 'event' ? 'share' : 'bot';
	return campaignTag(startParam) ?? 'other';
}

/**
 * PostHog loads in its own chunk (initial JS budget, DESIGN); calls made before it arrives are
 * queued and replayed in order. If it fails to load, analytics stay silent.
 */
function posthogClient(key: string, host: string): AnalyticsClient {
	let loaded: AnalyticsClient | null = null;
	let failed = false;
	const queue: ((client: AnalyticsClient) => void)[] = [];
	const run = (call: (client: AnalyticsClient) => void) => {
		if (loaded !== null) call(loaded);
		else if (!failed) queue.push(call);
	};

	import('posthog-js').then(
		({ default: posthog }) => {
			posthog.init(key, {
				api_host: host,
				person_profiles: 'identified_only',
				// Explicit events only: autocapture and pageviews could carry user text or names.
				autocapture: false,
				capture_pageview: false,
				capture_pageleave: false,
				// Session replay: all text and inputs masked (ANALYTICS → Setup rules).
				mask_all_text: true,
				mask_all_element_attributes: true,
				session_recording: { maskAllInputs: true, maskTextSelector: '*' },
			});
			loaded = posthog;
			for (const call of queue.splice(0)) call(posthog);
		},
		() => {
			failed = true;
			queue.length = 0;
		},
	);

	return {
		capture: (event, properties) => {
			run((client) => {
				client.capture(event, properties);
			});
		},
		identify: (distinctId) => {
			run((client) => {
				client.identify(distinctId);
			});
		},
		// Before load nothing is recording yet; a paused-then-resumed replay simply starts later.
		sessionRecordingStarted: () => loaded?.sessionRecordingStarted() ?? false,
		startSessionRecording: () => {
			run((client) => {
				client.startSessionRecording();
			});
		},
		stopSessionRecording: () => {
			run((client) => {
				client.stopSessionRecording();
			});
		},
	};
}

/** Call once at startup. Without a key (dev, tests) nothing is sent. */
export function initAnalytics(options: {
	key: string | undefined;
	host: string;
	startParam: string | null;
	platform: string | null;
	version: string | null;
	client?: AnalyticsClient;
}): void {
	const client =
		options.client ?? (options.key === undefined ? null : posthogClient(options.key, options.host));
	if (client === null) {
		state = null;
		return;
	}
	const link = parseStartParam(options.startParam);
	state = {
		client,
		source: sourceFromStartParam(options.startParam),
		platform: options.platform,
		version: options.version,
		viaNotification: notificationFromStartParam(options.startParam),
		deepLinkEventId: link?.kind === 'event' ? link.eventId : null,
		fromDeepLink: link !== null,
		replayBlocks: 0,
		replayWasOn: false,
	};
}

export function track<E extends keyof WebEvents>(event: E, properties: WebEvents[E]): void {
	if (state === null) return;
	const source = state.source;
	state.source = null;
	state.client.capture(event, source === null ? properties : { ...properties, source });
}

/** After backend auth: identify with the server-computed `analyticsId`, then `app_opened`. */
export function trackSignedIn(analyticsId: string, isNewUser: boolean): void {
	if (state === null) return;
	state.client.identify(analyticsId);
	track('app_opened', {
		is_new_user: isNewUser,
		platform: state.platform,
		tg_version: state.version,
		entry: state.fromDeepLink ? 'deeplink' : 'direct',
	});
	if (state.viaNotification !== null) {
		track('notification_clicked', { type: state.viaNotification, channel: 'bot' });
		state.viaNotification = null;
	}
}

/** Distance from the viewer to an event cell, bucketed (never the distance itself). */
export function distanceBucket(km: number | null): DistanceBucket {
	if (km === null) return 'unknown';
	if (km < 1) return 'lt_1km';
	if (km < 3) return '1_3km';
	if (km < 10) return '3_10km';
	return 'gt_10km';
}

/** True once for the event the session's deep link pointed at. */
export function takeDeepLinkEntry(eventId: string): boolean {
	if (state?.deepLinkEventId !== eventId) return false;
	state.deepLinkEventId = null;
	return true;
}

/** Pauses session replay while mounted (apply note, chat, profile edit). */
export function useNoReplay(): void {
	useEffect(() => {
		const current = state;
		if (current === null) return;
		current.replayBlocks += 1;
		if (current.replayBlocks === 1) {
			current.replayWasOn = current.client.sessionRecordingStarted();
			if (current.replayWasOn) current.client.stopSessionRecording();
		}
		return () => {
			current.replayBlocks -= 1;
			if (current.replayBlocks === 0 && current.replayWasOn) {
				current.client.startSessionRecording();
			}
		};
	}, []);
}

const UUID_SEGMENT = /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\/|$)/gi;

/** `/events/<uuid>/manage` → `/events/:id/manage`: screens without ids for `error_shown`. */
export function screenOf(pathname: string): string {
	return pathname.replace(UUID_SEGMENT, '/:id');
}

/** Every failed query or mutation the UI sees (wired into the QueryClient caches). */
export function trackApiError(error: unknown, pathname: string): void {
	const code =
		typeof error === 'object' && error !== null && 'code' in error && typeof error.code === 'string'
			? error.code
			: 'unknown';
	track('error_shown', { code, screen: screenOf(pathname) });
}
